import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const FilterPanel = ({ filters, onFiltersChange, onClearFilters }) => {
  const [expandedSections, setExpandedSections] = useState({
    jobCategories: true,
    aiScore: true,
    experience: true,
    location: false,
    applicationDate: false
  });

  const jobCategoryOptions = [
    { value: 'software-engineering', label: 'Software Engineering' },
    { value: 'data-science', label: 'Data Science' },
    { value: 'product-management', label: 'Product Management' },
    { value: 'design', label: 'UI/UX Design' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'sales', label: 'Sales' },
    { value: 'finance', label: 'Finance' },
    { value: 'hr', label: 'Human Resources' }
  ];

  const experienceLevelOptions = [
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (6-10 years)' },
    { value: 'lead', label: 'Lead/Principal (10+ years)' }
  ];

  const locationOptions = [
    { value: 'remote', label: 'Remote' },
    { value: 'new-york', label: 'New York, NY' },
    { value: 'san-francisco', label: 'San Francisco, CA' },
    { value: 'chicago', label: 'Chicago, IL' },
    { value: 'austin', label: 'Austin, TX' },
    { value: 'seattle', label: 'Seattle, WA' },
    { value: 'boston', label: 'Boston, MA' },
    { value: 'denver', label: 'Denver, CO' }
  ];

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleCheckboxChange = (category, value, checked) => {
    const currentValues = filters?.[category] || [];
    const newValues = checked 
      ? [...currentValues, value]
      : currentValues?.filter(v => v !== value);
    
    handleFilterChange(category, newValues);
  };

  const FilterSection = ({ title, isExpanded, onToggle, children }) => (
    <div className="border-b border-border pb-4 mb-4">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left mb-3 hover:text-primary transition-colors"
      >
        <h3 className="font-medium text-foreground">{title}</h3>
        <Icon 
          name={isExpanded ? "ChevronUp" : "ChevronDown"} 
          size={16} 
          className="text-muted-foreground"
        />
      </button>
      {isExpanded && children}
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6 h-fit">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-card-foreground flex items-center">
          <Icon name="Filter" size={20} className="mr-2" />
          Advanced Filters
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          iconName="X"
          iconSize={14}
        >
          Clear All
        </Button>
      </div>
      {/* Search */}
      <div className="mb-6">
        <Input
          type="search"
          placeholder="Search candidates..."
          value={filters?.search || ''}
          onChange={(e) => handleFilterChange('search', e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Job Categories */}
      <FilterSection
        title="Job Categories"
        isExpanded={expandedSections?.jobCategories}
        onToggle={() => toggleSection('jobCategories')}
      >
        <div className="space-y-3">
          {jobCategoryOptions?.map((option) => (
            <Checkbox
              key={option?.value}
              label={option?.label}
              checked={(filters?.jobCategories || [])?.includes(option?.value)}
              onChange={(e) => handleCheckboxChange('jobCategories', option?.value, e?.target?.checked)}
            />
          ))}
        </div>
      </FilterSection>
      {/* AI Score Range */}
      <FilterSection
        title="AI Eligibility Score"
        isExpanded={expandedSections?.aiScore}
        onToggle={() => toggleSection('aiScore')}
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <Input
              type="number"
              placeholder="Min"
              min="0"
              max="100"
              value={filters?.minScore || ''}
              onChange={(e) => handleFilterChange('minScore', e?.target?.value)}
              className="flex-1"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="number"
              placeholder="Max"
              min="0"
              max="100"
              value={filters?.maxScore || ''}
              onChange={(e) => handleFilterChange('maxScore', e?.target?.value)}
              className="flex-1"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            Score range: 0-100 (AI-generated compatibility score)
          </div>
        </div>
      </FilterSection>
      {/* Experience Level */}
      <FilterSection
        title="Experience Level"
        isExpanded={expandedSections?.experience}
        onToggle={() => toggleSection('experience')}
      >
        <div className="space-y-3">
          {experienceLevelOptions?.map((option) => (
            <Checkbox
              key={option?.value}
              label={option?.label}
              checked={(filters?.experienceLevels || [])?.includes(option?.value)}
              onChange={(e) => handleCheckboxChange('experienceLevels', option?.value, e?.target?.checked)}
            />
          ))}
        </div>
      </FilterSection>
      {/* Location */}
      <FilterSection
        title="Location"
        isExpanded={expandedSections?.location}
        onToggle={() => toggleSection('location')}
      >
        <Select
          placeholder="Select locations..."
          multiple
          searchable
          clearable
          options={locationOptions}
          value={filters?.locations || []}
          onChange={(value) => handleFilterChange('locations', value)}
        />
      </FilterSection>
      {/* Application Date */}
      <FilterSection
        title="Application Date"
        isExpanded={expandedSections?.applicationDate}
        onToggle={() => toggleSection('applicationDate')}
      >
        <div className="space-y-4">
          <Input
            type="date"
            label="From Date"
            value={filters?.dateFrom || ''}
            onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
          />
          <Input
            type="date"
            label="To Date"
            value={filters?.dateTo || ''}
            onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
          />
        </div>
      </FilterSection>
      {/* Active Filters Count */}
      {Object.values(filters)?.some(value => 
        Array.isArray(value) ? value?.length > 0 : value
      ) && (
        <div className="mt-6 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center text-sm text-primary">
            <Icon name="Filter" size={14} className="mr-2" />
            <span className="font-medium">
              {Object.values(filters)?.reduce((count, value) => {
                if (Array.isArray(value)) return count + value?.length;
                return value ? count + 1 : count;
              }, 0)} filters active
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;